import { useAddressSchema } from "@certn/cascadia/patterns/address-form/schema";
import { AddressErrors } from "@certn/cascadia/patterns/address-form/types";
import {
  initialAddressValues,
  isAddressErrors,
} from "@certn/cascadia/patterns/address-form/utils";
import {
  MinimalAddressForm,
  Grid,
  InnerContentItem,
  FieldErrors,
} from "@certn/cascadia-react";
import { slugifyPath } from "@certn/utils";
import { useContext, useEffect, useState } from "react";
import { ValidationError } from "yup";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

function getAddressWithUnit(address: any) {
  return address.unit_number
    ? `${address.unit_number}-${address.street_address}`
    : address.street_address;
}

export function AddressFormControl(props: CascadiaControlProps) {
  const [errors, setErrors] = useState<AddressErrors>();
  const { isSummary, isSubmitted } = useContext(CascadiaJsonFormContext);
  const addressSchema = useAddressSchema();
  useEffect(() => {
    const validate = async () => {
      try {
        await addressSchema.validate(props.data ?? initialAddressValues, {
          abortEarly: false,
        });
        setErrors({});
      } catch (e) {
        if (e instanceof ValidationError) {
          const parsedErrors = e.inner.reduce((acc, curr) => {
            if (!curr.path) {
              return acc;
            }

            return { ...acc, [curr.path]: curr.message };
          }, {});

          if (isAddressErrors(parsedErrors)) {
            setErrors(parsedErrors);
          }
        }
      }
    };

    validate();
  }, [props.data]);

  if (isSummary) {
    return (
      <InnerContentItem
        label={props.label}
        testID="address-group-summary-layout"
      >
        {getAddressWithUnit(props.data)}
        <br />
        {props.data?.locality || "-"},{" "}
        {props.data?.administrative_division_1 || "-"}{" "}
        {props.data?.country || "-"}
        <br />
        {props.data?.postal_code || "-"}
        <br />
      </InnerContentItem>
    );
  }

  return (
    <Grid form>
      <MinimalAddressForm
        errors={errors}
        value={props.data ?? initialAddressValues}
        onChange={props.setValue}
        onBlur={props.setTouched}
        isSubmitted={isSubmitted}
        testID={`${slugifyPath(props.path)}-address-form`}
        context={props.uischema?.options?.context}
      />
      {props.touched && <FieldErrors>{props.errors}</FieldErrors>}
    </Grid>
  );
}

AddressFormControl.displayName = "AddressFormControl";

export const AddressFormRenderer = withCascadiaControlProps(AddressFormControl);
